/**
 * PushNotificationToast
 *
 * Subscribes to foreground FCM messages (app is open) and displays them
 * as a MUI Snackbar toast in the top-right corner. Must be rendered inside
 * <Router> so it can use useNavigate for in-app routing.
 *
 * Background messages (app closed/hidden) are handled by the service worker
 * (public/firebase-messaging-sw.js) and display as OS-level notifications.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useAuth } from '../contexts/AuthContext';
import { onForegroundMessage } from '../frontend/NotificationHandler';
import logger from '../utils/logger';

const PRIORITY_SEVERITY = {
  urgent: 'error',
  high: 'warning',
  medium: 'info',
  low: 'info',
};

const PushNotificationToast = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [toast, setToast] = useState({
    open: false,
    title: '',
    body: '',
    actionUrl: null,
    severity: 'info',
  });

  useEffect(() => {
    if (!currentUser?.uid) return;

    const unsubscribe = onForegroundMessage((payload) => {
      try {
        const title =
          payload.notification?.title ||
          payload.data?.title ||
          'New Notification';
        const body =
          payload.notification?.body ||
          payload.data?.body ||
          '';
        const actionUrl = payload.data?.actionUrl || null;
        const priority = payload.data?.priority || 'medium';
        const severity = PRIORITY_SEVERITY[priority] || 'info';

        logger.info('FCM foreground message received:', { title, body, actionUrl });
        setToast({ open: true, title, body, actionUrl, severity });
      } catch (err) {
        logger.error('Error handling foreground message:', err);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [currentUser?.uid]);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setToast((t) => ({ ...t, open: false }));
  };

  const handleView = () => {
    setToast((t) => ({ ...t, open: false }));
    if (toast.actionUrl) {
      if (toast.actionUrl.startsWith('http')) {
        window.open(toast.actionUrl, '_blank');
      } else {
        navigate(toast.actionUrl);
      }
    }
  };

  return (
    <Snackbar
      open={toast.open}
      autoHideDuration={7000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ mt: 7 }} // clear the AppBar
    >
      <Alert
        severity={toast.severity}
        onClose={handleClose}
        sx={{ minWidth: 280, maxWidth: 380 }}
        action={
          toast.actionUrl ? (
            <Button size="small" color="inherit" onClick={handleView} sx={{ whiteSpace: 'nowrap' }}>
              View
            </Button>
          ) : undefined
        }
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {toast.title}
          </Typography>
          {toast.body && (
            <Typography variant="body2" sx={{ mt: 0.25, opacity: 0.9 }}>
              {toast.body}
            </Typography>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default PushNotificationToast;
